import { usePermissions } from "../../../session/permissions";
import { useWorkspaceId } from "../../../session/workspace-context";
import { serviceKeys } from "../../service/service-keys";
import { useServiceAreaAccess } from "../../service/use-service-area";
import { useServiceQuery } from "../../service/use-service-query";

const LINKED_TICKETS_LIMIT = 20;
const LINKED_STALE_MS = 30_000;

export interface TicketSupport {
  /** The workspace has the Service Desk and the member may read tickets: the inbox then shows ticket chips and links. */
  canSee: boolean;
  /** The member may also raise one from a chat (`tickets.reply`, not in a read-only session). */
  canConvert: boolean;
}

/**
 * Whether the inbox offers the Service Desk pieces. The server's nav alone says whether the workspace has it (and the plan
 * includes it), so a workspace without it makes no ticket request at all and the inbox looks as it always did.
 */
export function useTicketSupport(): TicketSupport {
  const permissions = usePermissions();
  const access = useServiceAreaAccess("tickets");
  const canSee = access.enabled && !access.locked && permissions.can("tickets.view", { anywhere: true });
  return { canSee, canConvert: canSee && permissions.can("tickets.reply", { anywhere: true }) && !permissions.readOnly };
}

/** The tickets raised from, or linked to, one chat (`GET /v1/tickets?conversation_id=`), newest update first. */
export function useConversationTickets(conversationId: string, enabled: boolean) {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: [...serviceKeys.tickets(ws), "by-conversation", conversationId],
    enabled,
    staleTime: LINKED_STALE_MS,
    request: (api, signal) => api.GET("/v1/tickets", { params: { query: { conversation_id: conversationId, limit: LINKED_TICKETS_LIMIT } }, signal }),
  });
}
